/**
 * Car classification codes and the search categories built on top of them.
 *
 * The cars-search API classifies every vehicle as 1 (basic), 2 (premium) or
 * 3 (luxury). END-86 collapsed the three user-facing search filters into two:
 * "פרימיום" now covers both premium and luxury vehicles, and "בסיסי" was
 * renamed "סטנדרטי". The API still returns all three codes, so the raw enum
 * stays intact and the merge happens in the category mapping below.
 *
 * @example
 * // Codes to request for the פרימיום filter
 * CarTypeToClassificationCodes.Premium // [2, 3]
 *
 * // Which filter a vehicle's classification code belongs to
 * getCarTypeFromClassificationCode(3) // "Premium"
 */

/** Raw vehicle classification codes as returned by the cars-search API. */
export enum CarTypeId {
  Basic = 1,
  Premium = 2,
  Luxury = 3,
}

/** The two search categories exposed to users (END-86). */
export type CarType = "Standard" | "Premium"

/** Hebrew labels for the search categories. */
export const CarTypeLabels: Record<CarType, string> = {
  Premium: "פרימיום",
  Standard: "סטנדרטי",
}

/**
 * Tab order shared by the search page filters and the homepage search block.
 * The index doubles as the homepage block's `planLevel`.
 */
export const CarTypeOrder: readonly CarType[] = ["Premium", "Standard"]

/**
 * API classification codes behind each search category. Premium requests both
 * 2 and 3 so no luxury inventory disappears from the merged filter — leasing in
 * particular is currently classified almost entirely as 3.
 */
export const CarTypeToClassificationCodes: Record<CarType, CarTypeId[]> = {
  Premium: [CarTypeId.Premium, CarTypeId.Luxury],
  Standard: [CarTypeId.Basic],
}

/** Maps a raw API classification code to the search category that now contains it. */
export function getCarTypeFromClassificationCode(
  code: number | null | undefined,
): CarType {
  return code === CarTypeId.Basic ? "Standard" : "Premium"
}

/**
 * `carType` URL values used before END-86. Old links, CMS-authored links and
 * anything a user has bookmarked still resolve to the merged categories.
 */
const LEGACY_CAR_TYPES: Record<string, CarType> = {
  Basic: "Standard",
  Premium: "Premium",
  Luxury: "Premium",
}

/**
 * Normalises a `carType` value coming from a URL or the CMS, accepting the
 * pre-END-86 names. Returns null when the value is not a known category.
 */
export function normalizeCarType(
  value: string | null | undefined,
): CarType | null {
  if (!value) return null
  if (value === "Premium" || value === "Standard") return value
  return LEGACY_CAR_TYPES[value] ?? null
}
